import axios from 'axios';
import { API_URL } from './config';

const API = API_URL;

// Token management
const TOKEN_KEY = 'fira_auth_token';
const REFRESH_TOKEN_KEY = 'fira_refresh_token';
const USER_KEY = 'fira_user';
const TOKEN_EXPIRY_KEY = 'fira_token_expiry';

export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const getStoredRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const getStoredUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const getTokenExpiry = () => {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  return expiry ? parseInt(expiry) : null;
};

export const setAuthData = (token, user, refreshToken = null) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // Set token expiry (24 hours from now)
  const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  // Set default header for all axios requests
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

// Check if token is expired or will expire soon (within 1 hour)
export const isTokenExpiringSoon = () => {
  const expiry = getTokenExpiry();
  if (!expiry) return true; // No expiry set, assume expired
  const oneHourFromNow = Date.now() + (60 * 60 * 1000);
  return expiry < oneHourFromNow;
};

// Check if token is completely expired
export const isTokenExpired = () => {
  const expiry = getTokenExpiry();
  if (!expiry) return true;
  return Date.now() > expiry;
};

// Initialize axios with stored token on app load
export const initializeAuth = () => {
  const token = getStoredToken();
  if (token) {
    // Check if token is expired
    if (isTokenExpired()) {
      console.log('[AUTH] Token expired, clearing auth data');
      clearAuthData();
      return false;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return true;
  }
  return false;
};

// Auth API calls
export const register = async (email, password, name, phone = null) => {
  console.log('[AUTH] Attempting registration...');
  const response = await axios.post(`${API}/auth/register`, {
    email,
    password,
    name,
    phone
  });
  
  console.log('[AUTH] Registration response:', response.data.success);
  
  if (response.data.success) {
    setAuthData(response.data.access_token, response.data.user);
    console.log('[AUTH] Auth data saved to localStorage');
  }
  
  return response.data;
};

// Register with Invite Code
export const registerWithInvite = async (inviteCode, email, password, name, phone = null) => {
  console.log('[AUTH] Attempting registration with invite code...');
  const response = await axios.post(`${API}/auth/register-with-invite`, {
    invite_code: inviteCode,
    email,
    password,
    name,
    phone
  });
  
  console.log('[AUTH] Invite registration response:', response.data.success);
  
  if (response.data.success) {
    setAuthData(response.data.access_token, response.data.user);
    console.log('[AUTH] Auth data saved to localStorage');
  }
  
  return response.data;
};

// Validate invite code
export const validateInvite = async (code) => {
  const response = await axios.get(`${API}/invite/validate/${code}`);
  return response.data;
};

export const login = async (email, password) => {
  console.log('[AUTH] Attempting login to:', `${API}/auth/login`);
  console.log('[AUTH] API URL:', API);
  
  const maxRetries = 3;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, { timeout: 15000 }); // 15s timeout per attempt
      
      console.log('[AUTH] Login response status:', response.status);
      console.log('[AUTH] Login response success:', response.data.success);
      
      if (response.data.success) {
        setAuthData(response.data.access_token, response.data.user, response.data.refresh_token);
        console.log('[AUTH] Auth data saved to localStorage (with refresh token)');
        console.log('[AUTH] Token (first 50 chars):', response.data.access_token?.substring(0, 50));
      }
      
      return response.data;
    } catch (error) {
      console.error(`[AUTH] Login error (attempt ${attempt}/${maxRetries}):`, error.message);
      
      // Retry on 520/502/503/504 or network errors (server busy, not user error)
      const isServerBusy = error.response?.status >= 500 || 
                           error.code === 'ECONNABORTED' || 
                           error.code === 'ERR_NETWORK' ||
                           error.message?.includes('timeout') ||
                           error.message?.includes('Network Error');
      
      if (isServerBusy && attempt < maxRetries) {
        console.log(`[AUTH] Server busy, retrying in ${attempt * 2}s... (${attempt}/${maxRetries})`);
        await new Promise(r => setTimeout(r, attempt * 2000));
        continue;
      }
      
      // Final attempt failed or non-retryable error
      console.error('[AUTH] Error response:', error.response?.data);
      console.error('[AUTH] Error status:', error.response?.status);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return { success: false, error: 'Server slow to respond. Please try again.' };
      }
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        return { success: false, error: 'Unable to connect to server. Please check your internet connection.' };
      }
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail || error.response?.data?.error || '';
        return { success: false, error: detail || 'Invalid email or password.' };
      }
      if (error.response?.status === 403) {
        return { success: false, error: 'Account access denied. Please contact admin.' };
      }
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail || '';
        return { success: false, error: detail || 'Too many attempts. Please wait a moment.' };
      }
      if (error.response?.status >= 500) {
        return { success: false, error: 'Server is busy. Please try again in a few seconds.' };
      }
      
      return { success: false, error: error.response?.data?.detail || 'An error occurred. Please try again.' };
    }
  }
};

export const logout = () => {
  clearAuthData();
  window.location.href = '/';
};

export const getCurrentUser = async () => {
  const response = await axios.get(`${API}/auth/me`);
  return response.data;
};

export const refreshToken = async () => {
  try {
    const storedRefreshToken = getStoredRefreshToken();
    if (!storedRefreshToken) {
      console.log('[AUTH] No refresh token found');
      return false;
    }
    
    const response = await axios.post(`${API}/auth/refresh`, {
      refresh_token: storedRefreshToken
    }, {
      timeout: 10000, // 10s timeout (was 30s — too long)
      _skipInterceptor: true // Don't let interceptor retry this request
    });
    
    if (response.data.access_token) {
      const user = getStoredUser();
      setAuthData(response.data.access_token, user, response.data.refresh_token);
      console.log('[AUTH] Token refreshed successfully');
      return true;
    }
  } catch (error) {
    console.error('[AUTH] Token refresh failed:', error.response?.status);
    // Clear auth data and force re-login
    clearAuthData();
    // Clear premium cache too
    try { localStorage.removeItem('fira_premium_cache'); } catch(e) {}
  }
  return false;
};

// Check and refresh token if needed (call this on app resume/visibility change)
export const checkAndRefreshToken = async () => {
  if (!getStoredToken()) return false;
  
  // If token is expiring soon, try to refresh
  if (isTokenExpiringSoon()) {
    console.log('[AUTH] Token expiring soon, attempting refresh...');
    return await refreshToken();
  }
  return true;
};

// Setup axios interceptor for auto token refresh + server busy retry
let _isRefreshing = false;  // Prevent concurrent refresh attempts

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retried — token expired, refresh it
    // Skip if this IS the refresh request itself (prevent infinite loop)
    if (error.response?.status === 401 && !originalRequest._retry && 
        !originalRequest.url?.includes('/auth/refresh') && !originalRequest._skipInterceptor) {
      originalRequest._retry = true;
      
      if (!_isRefreshing) {
        _isRefreshing = true;
        const refreshed = await refreshToken();
        _isRefreshing = false;
        
        if (refreshed) {
          originalRequest.headers['Authorization'] = `Bearer ${getStoredToken()}`;
          return axios(originalRequest);
        }
      }
      // Refresh failed — don't retry, let user re-login
    }
    
    // v8.6: Auto-retry on server busy (502/503/504/520) — max 2 retries with backoff
    const isServerBusy = [502, 503, 504, 520, 429].includes(error.response?.status) ||
                         error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
    const retryCount = originalRequest._retryCount || 0;
    
    if (isServerBusy && retryCount < 2 && !originalRequest.url?.includes('/signals/generate')) {
      originalRequest._retryCount = retryCount + 1;
      const delay = (retryCount + 1) * 1500; // 1.5s, 3s
      console.log(`[AXIOS] Server busy (${error.response?.status}), retry ${retryCount + 1}/2 in ${delay}ms`);
      await new Promise(r => setTimeout(r, delay));
      return axios(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.post(`${API}/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

// Forgot Password - Request reset link
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API}/auth/forgot-password`, { email });
  return response.data;
};

// Reset Password - Set new password with token
export const resetPassword = async (token, newPassword) => {
  const response = await axios.post(`${API}/auth/reset-password`, {
    token,
    new_password: newPassword
  });
  return response.data;
};

// Verify Reset Token
export const verifyResetToken = async (token) => {
  const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
  return response.data;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getStoredToken();
};
